import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Server, Palette, Save, RefreshCw, Eye, EyeOff } from "lucide-react";

const PayPalAdmin = () => {
  const [paypalData, setPaypalData] = useState({
    clientId: "",
    clientSecret: "",
    mode: "sandbox",
    currency: "USD",
    enablePayPal: true,
    buttonStyles: {
      layout: "vertical",
      color: "blue",
      shape: "rect",
      label: "paypal"
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [error, setError] = useState(null);
  const [showSecret, setShowSecret] = useState(false);



  useEffect(() => {
    fetchPayPalData();
  }, []);

  const fetchPayPalData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/paypal-settings');
      if (response.data.success) {
        setPaypalData(response.data.data);
      } else {
        setError('Failed to fetch PayPal data');
      }
    } catch (err) {
      console.error("Error fetching PayPal data:", err);
      setError('Error fetching PayPal data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleToggleChange = (name) => {
    setPaypalData(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleButtonStyleChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      buttonStyles: {
        ...prev.buttonStyles,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await axios.put('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/paypal-settings', paypalData);
      if (response.data.success) {
        toast.success('PayPal settings updated successfully');
      } else {
        toast.error('Failed to update PayPal settings');
      }
    } catch (err) {
      console.error("Error updating PayPal settings:", err);
      toast.error('Error updating PayPal settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all PayPal settings to default?')) {
      try {
        setSaving(true);
        const response = await axios.post('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/paypal-settings/reset');
        if (response.data.success) {
          toast.success('PayPal settings reset successfully');
          fetchPayPalData();
        } else {
          toast.error('Failed to reset PayPal settings');
        }
      } catch (err) {
        console.error("Error resetting PayPal settings:", err);
        toast.error('Error resetting PayPal settings');
      } finally {
        setSaving(false);
      }
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await axios.post('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/paypal-settings/test-connection', {
        clientId: paypalData.clientId,
        clientSecret: paypalData.clientSecret,
        mode: paypalData.mode
      });
      
      if (response.data.success) {
        toast.success('PayPal connection test successful!');
      } else {
        toast.error('PayPal connection test failed');
      }
    } catch (err) {
      console.error("Error testing PayPal connection:", err);
      toast.error(`PayPal connection test failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const renderPayPalButtonPreview = () => {
    const { color, shape, label } = paypalData.buttonStyles;
    
    // Dynamic styles based on button settings
    const buttonStyle = {
      padding: '10px 20px',
      cursor: 'not-allowed',
      fontSize: '16px',
      display: 'inline-block',
      fontWeight: 'bold',
      textAlign: 'center',
      width: '200px',
      position: 'relative',
      backgroundColor: color === 'gold' ? '#FFC439' : 
                       color === 'blue' ? '#169BD7' : 
                       color === 'silver' ? '#EEEEEE' : 
                       color === 'white' ? '#FFFFFF' : 
                       color === 'black' ? '#000000' : '#169BD7',
      color: ['white', 'black'].includes(color) ? 
             (color === 'white' ? '#000000' : '#FFFFFF') : '#FFFFFF',
      borderRadius: shape === 'pill' ? '50px' : shape === 'rect' ? '4px' : '0',
    };

    // Label text
    const labelText = label === 'paypal' ? 'PayPal' :
                      label === 'checkout' ? 'Checkout' :
                      label === 'buynow' ? 'Buy Now' :
                      label === 'pay' ? 'Pay with PayPal' : 'PayPal';

    return (
      <div className="border rounded p-3 text-center mt-3 bg-light">
        <p className="small mb-2">Button Preview:</p>
        <div style={buttonStyle}>
          {labelText}
        </div>
        <p className="small text-muted mt-2 mb-0">
          (Preview only - actual button will be rendered by PayPal SDK)
        </p>
      </div>
    );
  };

  if (loading) return <div className="p-5 text-center">Loading PayPal settings...</div>;
  
  if (error) return <div className="alert alert-danger m-4">
    Error: {error}
  </div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">PayPal Integration Settings</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${paypalData.enablePayPal ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={() => handleToggleChange('enablePayPal')}
                type="button"
            >
                {paypalData.enablePayPal ? <Eye size={18} /> : <EyeOff size={18} />}
                {paypalData.enablePayPal ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving} type="button">
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving} type="button">
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* General Settings */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <Server size={20} /> General Settings
                    </div>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Mode</label>
                            <select 
                              name="mode" 
                              value={paypalData.mode} 
                              onChange={handleChange}
                              className="form-select"
                            >
                              <option value="sandbox">Sandbox (Testing)</option>
                              <option value="live">Live (Production)</option>
                            </select>
                            <div className="form-text small text-white">Use Sandbox mode for testing, and Live mode for real payments.</div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Currency</label>
                            <select 
                              name="currency" 
                              value={paypalData.currency} 
                              onChange={handleChange}
                              className="form-select"
                            >
                              <option value="USD">US Dollar (USD)</option>
                              <option value="EUR">Euro (EUR)</option>
                              <option value="GBP">British Pound (GBP)</option>
                              <option value="CAD">Canadian Dollar (CAD)</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Client ID</label>
                        <input 
                          type="text" 
                          name="clientId" 
                          value={paypalData.clientId} 
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter PayPal Client ID"
                        />
                        <div className="form-text small text-white">Available in your PayPal Developer Dashboard</div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label small fw-bold">Client Secret</label>
                        <div className="input-group">
                            <input 
                              type={showSecret ? "text" : "password"} 
                              name="clientSecret" 
                              value={paypalData.clientSecret || ""} 
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Enter PayPal Client Secret"
                            />
                            <button 
                              type="button" 
                              onClick={() => setShowSecret(!showSecret)}
                              className="btn btn-outline-secondary"
                              title={showSecret ? "Hide Secret" : "Show Secret"}
                            >
                              {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <div className="form-text small text-white">Available in your PayPal Developer Dashboard. This is required for secure server-side order capture.</div>
                    </div>

                    <button 
                        type="button" 
                        onClick={testConnection}
                        className="btn btn-info d-flex align-items-center gap-2 text-white"
                        disabled={testingConnection || !paypalData.clientId || !paypalData.clientSecret}
                    >
                        <RefreshCw size={16} className={testingConnection ? 'fa-spin' : ''} /> 
                        {testingConnection ? 'Testing Connection...' : 'Test PayPal Connection'}
                    </button>
                </div>
            </div>
          </div>

          {/* Button Appearance */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Palette size={20} /> Button Appearance
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3 mb-3">
                            <label className="form-label small fw-bold">Layout</label>
                            <select 
                              name="layout" 
                              value={paypalData.buttonStyles.layout} 
                              onChange={handleButtonStyleChange}
                              className="form-select"
                            >
                              <option value="vertical">Vertical</option>
                              <option value="horizontal">Horizontal</option>
                            </select>
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label small fw-bold">Color</label>
                            <select 
                              name="color" 
                              value={paypalData.buttonStyles.color} 
                              onChange={handleButtonStyleChange}
                              className="form-select"
                            >
                              <option value="gold">Gold</option>
                              <option value="blue">Blue</option>
                              <option value="silver">Silver</option>
                              <option value="white">White</option>
                              <option value="black">Black</option>
                            </select>
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label small fw-bold">Shape</label>
                            <select 
                              name="shape" 
                              value={paypalData.buttonStyles.shape} 
                              onChange={handleButtonStyleChange}
                              className="form-select"
                            >
                              <option value="rect">Rectangle</option>
                              <option value="pill">Pill</option>
                            </select>
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label small fw-bold">Label</label>
                            <select 
                              name="label" 
                              value={paypalData.buttonStyles.label} 
                              onChange={handleButtonStyleChange}
                              className="form-select"
                            >
                              <option value="paypal">PayPal</option>
                              <option value="checkout">Checkout</option>
                              <option value="buynow">Buy Now</option>
                              <option value="pay">Pay with PayPal</option>
                            </select>
                        </div>
                    </div>
                    
                    {renderPayPalButtonPreview()}
                </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PayPalAdmin;